import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";

export interface EmptyStateProps {
  /** Lucide icon rendered above the title. */
  icon: LucideIcon;
  /** One-line title. */
  title: string;
  /** One-line supporting copy. */
  description: string;
  /** Optional single call-to-action (button, link, etc.). */
  action?: React.ReactNode;
  /** `md` (default) for full-surface empties; `sm` for in-card / in-table. */
  size?: "sm" | "md";
  className?: string;
}

const SIZE_STYLES = {
  sm: {
    wrapper: "py-8",
    icon: "h-8 w-8 mb-3",
    title: "text-base font-medium",
    description: "text-sm max-w-xs mt-1",
  },
  md: {
    wrapper: "py-12",
    icon: "h-12 w-12 mb-4",
    title: "text-lg font-medium",
    description: "text-sm max-w-sm mt-1",
  },
} as const;

/**
 * Canonical empty-state block (DESIGN.md §15.2): icon, one-line title, one-line
 * subtitle, optional single CTA. Use inside tables/cards (`size="sm"`) or as a
 * full-surface empty (`size="md"`).
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  size = "md",
  className,
}: EmptyStateProps) {
  const s = SIZE_STYLES[size];
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        s.wrapper,
        className,
      )}
    >
      <Icon className={cn("text-muted-foreground", s.icon)} aria-hidden="true" />
      <p className={s.title}>{title}</p>
      <p className={cn("text-muted-foreground", s.description)}>{description}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}
